import React from "react";
import { Route, Routes } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useAuthorization } from "@common/providers";
import { useGetProjectByIdQuery, useWhoAmIQuery } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useListContacts from "./api/useListContacts";
import useListContactProjectVariables from "./api/useListContactProjectVariables";
import Show from "./Show/Show";
import Edit from "./Edit/Edit";
import New from "./New/New";

interface Props {
  project_id: string;
}

const ProjectContactsV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const { data: project, isLoading: projectLoading } = useGetProjectByIdQuery({
    id: projectId as any,
    slug: accountSlug,
    include: ["client"],
  });

  const { data: currentUser, isLoading: currentUserLoading } = useWhoAmIQuery();

  const { contacts, contactsLoading } = useListContacts({ projectId });

  const { variables, variablesLoading } = useListContactProjectVariables();

  const authorized = useAuthorization();

  if (
    !authorized ||
    projectLoading ||
    currentUserLoading ||
    contactsLoading ||
    variablesLoading ||
    !project ||
    !currentUser
  ) {
    return <ScopeStackSpinner />;
  }

  const projectStatus = project?.data?.attributes?.status || "";
  const privileges = currentUser?.data?.attributes?.privileges || [];
  const permission =
    privileges.find((p) => p.privilege === "projects.contacts")?.[
      "access-level"
    ] || "view";

  const clientRelationship = project?.data?.relationships?.client?.data;
  const clientId = clientRelationship
    ? String((clientRelationship as any).id)
    : null;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Show
            projectId={projectId}
            projectStatus={projectStatus}
            permission={permission}
          />
        }
      />
      <Route
        path="/edit/:contactId"
        element={
          <Edit
            projectId={projectId}
            permission={permission}
            projectStatus={projectStatus}
            contacts={contacts}
            clientId={clientId}
            projectVariableDefinitions={variables}
          />
        }
      />
      <Route
        path="/new"
        element={
          <New
            projectId={projectId}
            permission={permission}
            projectStatus={projectStatus}
            contacts={contacts}
            clientId={clientId}
            projectVariableDefinitions={variables}
          />
        }
      />
    </Routes>
  );
};

export default ProjectContactsV2Mspa;
